import type { RouteDef } from '../router';
import { trialCtaHtml } from '../trial-cta';
import { articlesPending, getBlogPostSources, hasBlogPost, listBlogArticles } from '../data/articles';
import { escapeHtml } from '../utils';
import { t, getLocale } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { blogPostHtml, mergeBlogPost, relatedPosts, type BlogPostData } from '@roamzy/shared';

const blogArticle: RouteDef = {
  path: '/blog/:slug',
  whenReady: articlesPending,
  routeType: 'article',
  navKey: 'blog',
  metaTitle: 'Roamzy Journal',
  metaDesc: 'Travel eSIM guides and comparisons.',
  render(ctx) {
    const slug = ctx.params.slug ?? '';
    // Field by field, as the server resolves it: a translation missing a
    // field (three es and two pt posts have no read time) takes the English one.
    const { localized, en } = getBlogPostSources(slug);
    const a = mergeBlogPost(localized, en);

    if (!a || !a.h1) {
      return `
<section class="section">
  <div class="container" style="text-align:center; padding:120px 0;">
    <div class="section__kicker" data-i18n="blogArticle.notFound.kicker">${escapeHtml(t('blogArticle.notFound.kicker'))}</div>
    <h1 style="font-style:italic; font-size:clamp(2.5rem,6vw,4.5rem);">${escapeHtml(t('blogArticle.notFound.title'))}</h1>
    <p style="margin-top:24px;"><a href="/blog" data-i18n="blogArticle.notFound.back">${escapeHtml(t('blogArticle.notFound.back'))}</a></p>
  </div>
</section>`;
    }

    // The tab title and description come from the server's meta for this URL
    // (seo-overrides.ts, after every navigation).

    // One renderer with the server (shared/blog-post.ts): the redraw of the
    // server's post changes nothing (2026-09-30).
    const catalog = listBlogArticles().map(([k, v]) => [k.replace(/^blog\//, ''), v as BlogPostData] as const);
    return `<article class="article-route">${blogPostHtml({
      locale: getLocale(),
      slug,
      post: a,
      related: relatedPosts(slug, a.category, catalog),
      t: (key, vars) => t(key as TranslationKey, vars),
      hasBlogTranslation: hasBlogPost,
      trialCtaHtml: trialCtaHtml(),
    })}</article>`;
  },
  mount(root) {
    // /blog/x#faq: the browser's own jump came before this redraw and the
    // router's scroll to the top, so make it again once the page is in place.
    let id = window.location.hash.slice(1);
    try { id = decodeURIComponent(id); } catch { /* a malformed escape stays as typed */ }
    const target = id ? document.getElementById(id) : null;
    if (target && root.contains(target)) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
  },
};

export default blogArticle;
